<template>
  <div class="map-view">
    <NavBar
      :onUserIconClick="onUserIconClick"
      :udSelected="udSelected"
      :toggleUdSelected="toggleUdSelected"
    />
    <div class="content">
      <NavTouch
        :mapViewSelected="mapViewSelected"
        :setMapViewSelected="setMapViewSelected"
      />
      <itinerary-edit
        :itineraries="state.itineraries"
        :selectedItineraryIndex="this.selectedItineraryIndex"
        :fromPlace="this.fromPlace"
        :toPlace="this.toPlace"
        :startTime="this.startTime"
        :transportModes="this.transportModes"
        :viaPlace="this.viaPlace"
        :selectItinerary="this.selectItinerary"
        :onTimeEditClick="this.openDateTimePicker"
        :toggleEditMode="this.toggleEditMode"
        :editMode="this.editMode"
        :fromPlaceSelected="this.setFromPlace"
        :toPlaceSelected="this.setToPlace"
        :toggleTransportMode="this.toggleTransportMode"
        :tripType="this.tripType"
        :removeCycleTo="this.removeCycleTo"
        :walkReluctance="this.walkReluctance"
        :setWalkReluctance="this.setWalkReluctance"
        :udSelected="udSelected"
        :returTripActive="returTripActive"
        :onReturTripActiveToggle="onReturTripActiveToggle"
      />
      <save-button
        v-if="state.itineraries.length"
        :onClick="saveTrip"
      />
      <div
        id="trip-edit-map"
        :style="{ zIndex: mapViewSelected ? '3' :  '1' }"
      >
        <a
          id="septimalogo"
          href="https://www.septima.dk"
        >
          <SeptimaLogo
            :height="18"
            :iconColor="'rgba(0,0,0,0.5)'"
          />
        </a></div>
      <datetime
        type="datetime"
        ref="date-picker"
        :value="startTime"
        @input="setStartTime"
        :auto="true"
      >
        <template slot="button-cancel">
          Fortryd
        </template>
        <template slot="button-confirm">
          OK
        </template>
      </datetime>
      <SaveModal
        v-if="showSaveModal"
        :close="closeSaveModel"
        :id="this.state.trip.id"
      />
    </div>
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox.scss';
import { addHours } from 'date-fns';
import { Datetime } from 'vue-datetime';
import 'vue-datetime/dist/vue-datetime.css';
import { createMap, setMapItinerary, showStep } from '@/mapUtil';
import { layers, loadLayers } from '@/mapUtil/layers';
import NavBar from '@/components/NavBar/NavBar';
import NavTouch from '@/components/NavTouch/NavTouch';
import ItineraryEdit from '@/components/Itinerary/ItineraryEdit';
import busOval from '../assets/busOval.svg';
import cykel from '../assets/cykel.svg';
import SaveButton from '@/components/Itinerary/SaveButton';
import SaveModal from '@/components/Itinerary/SaveModal';
import SeptimaLogo from '@/components/Icons/SeptimaLogo.vue';
import { tripType } from '@/util/otp';
import {
  loadTrip,
  saveTrip,
  toggleUdSelected,
  getMarker,
  selectItinerary,
  onUserIconClick,
  getItineraries,
  setFromPlace,
  setToPlace,
  getFarligKrydsPopupHtml,
  getCykelstiPopupHtml,
  setMapViewSelected,
  resetItinerary,
  clearItineraries,
} from '@/util/itinerary';
// import { debug } from 'util';

export default {
  name: 'edit-trip',
  components: {
    NavBar,
    NavTouch,
    ItineraryEdit,
    Datetime,
    SaveButton,
    SaveModal,
    SeptimaLogo,
  },
  data() {
    return {
      setMapItinerary: setMapItinerary.bind(this),
      mapViewSelected: false,
      clearItineraries: clearItineraries.bind(this),
      resetItinerary: resetItinerary.bind(this),
      setMapViewSelected: setMapViewSelected.bind(this),
      loadTrip: loadTrip.bind(this),
      saveTrip: saveTrip.bind(this),
      toggleUdSelected: toggleUdSelected.bind(this),
      selectItinerary: selectItinerary.bind(this),
      onUserIconClick: onUserIconClick.bind(this),
      getItineraries: getItineraries.bind(this),
      setFromPlace: setFromPlace.bind(this),
      setToPlace: setToPlace.bind(this),
      getFarligKrydsPopupHtml: getFarligKrydsPopupHtml.bind(this),
      getCykelstiPopupHtml: getCykelstiPopupHtml.bind(this),
      getMarker,
      state: this.$store.state,
      map: null,
      mapPopup: new Mapbox.Popup({
        closeButton: false,
        closeOnClick: false,
      }),
      error: '',
      udSelected: true,
      editMode: undefined, // undefined || 'fromPlace' || 'toPlace'
      fromPlaceMarker: undefined,
      toPlaceMarker: undefined,
      loading: false,
      showSaveModal: false,
      walkReluctance: 2,
      returTripActive: false,
      unsubscribe: undefined,
    };
  },
  computed: {
    tripType: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud
            ? this.state.trip.ud.tripType
            : tripType.standard;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.tripType
          : tripType.standard;
      },
    },
    selectedItineraryIndex: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud
            ? this.state.trip.ud.selectedItineraryIndex
            : 0;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.selectedItineraryIndex
          : 0;
      },
    },
    ud: {
      cache: false,
      get: function() {
        return this.state.trip.ud;
      },
    },
    retur: {
      cache: false,
      get: function() {
        return this.state.trip.retur;
      },
    },
    fromPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud ? this.state.trip.ud.fromPlace : undefined;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.fromPlace
          : undefined;
      },
    },
    toPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud ? this.state.trip.ud.toPlace : undefined;
        }
        return this.state.trip.retur
          ? this.state.trip.retur.toPlace
          : undefined;
      },
    },
    viaPlace: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.ud ? this.ud.viaPlace : undefined;
        }
        return this.retur ? this.retur.viaPlace : undefined;
      },
    },
    startTime: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud.startTime;
        }
        if (this.state.trip.retur) {
          return this.state.trip.retur.startTime;
        }
        return undefined;
      },
    },
    transportModes: {
      cache: false,
      get: function() {
        if (this.udSelected) {
          return this.state.trip.ud.transportModes;
        }
        if (this.state.trip.retur) {
          return this.state.trip.retur.transportModes;
        }
        return undefined;
      },
    },
    itinerary: function() {
      const { itineraries } = this.state;
      return this.selectedItineraryIndex > -1
        ? itineraries[this.selectedItineraryIndex]
        : null;
    },
    user() {
      return this.state.user;
    },
  },
  methods: {
    setStartTime(payload) {
      this.$store.commit('setStartTime', {
        startTime: payload,
        udSelected: this.udSelected,
      });
      const { itineraries } = this.state;
      if ((itineraries.length && this.fromPlace, this.toPlace)) {
        this.getItineraries();
      }
    },
    onReturTripActiveToggle() {
      this.returTripActive = !this.returTripActive;
      if (this.returTripActive) {
        const udTrip = this.$store.state.trip.ud;
        const {
          transportModes,
          itineraryModes,
          toPlace,
          fromPlace,
          startTime,
          viaPlace,
        } = udTrip;
        const returTripType =
          itineraryModes.length > 1 &&
          itineraryModes.indexOf('BICYCLE') > -1 &&
          (itineraryModes.indexOf('RAIL') > -1 ||
            itineraryModes.indexOf('BUS') > -1)
            ? tripType.bicycleHome
            : tripType.standard;
        this.$store.commit('setTripRetur', {
          tripType: returTripType,
          fromPlace: toPlace,
          toPlace: fromPlace,
          startTime: addHours(new Date(startTime), 6).toISOString(),
          transportModes: { ...transportModes },
          selectedItineraryIndex: 0,
          viaPlace: viaPlace,
        });
        if (this.fromPlace) {
          this.fromPlaceMarker = this.getMarker(
            this.fromPlace.point,
            'A'
          ).addTo(this.map);
        }
        if (this.toPlace) {
          this.toPlaceMarker = this.getMarker(this.toPlace.point, 'B').addTo(
            this.map
          );
        }
        this.getItineraries();
      } else {
        if (this.fromPlaceMarker) {
          this.fromPlaceMarker.remove();
        }
        if (this.toPlaceMarker) {
          this.toPlaceMarker.remove();
        }
        this.$store.commit('setTripRetur', undefined);
        this.resetItinerary({ resetReturTrip: true });
      }
    },
    setWalkReluctance(wr) {
      this.walkReluctance = wr;
      this.getItineraries();
    },
    removeCycleTo() {
      this.$store.commit('setTripType', {
        tripType: tripType.standard,
        udSelected: this.udSelected,
      });
      this.$store.commit('setViaPlace', {
        viaPlace: undefined,
        udSelected: this.udSelected,
      });
      this.getItineraries();
    },
    closeSaveModel() {
      this.$router.push(`/edit/${this.state.trip.id}`);
      this.showSaveModal = false;
    },
    toggleTransportMode(mode) {
      this.$store.commit('toggleTransportMode', {
        mode,
        udSelected: this.udSelected,
      });
      this.$store.commit('setTripType', {
        tripType: tripType.standard,
        udSelected: this.udSelected,
      });
      this.getItineraries();
    },
    toggleEditMode(mode) {
      if (!this.editMode || this.editMode !== mode) {
        this.editMode = mode;
      } else {
        this.editMode = undefined;
      }
    },
    openDateTimePicker() {
      this.$refs['date-picker'].$el.children[0].click();
    },

    getBusStopPopupHtml(feature) {
      const { name } = feature.properties;
      return `
        <div class="bus-popup-container">
          <div class="popup-flex-row"><img src=${busOval}><h1>${name}</h1></div>
          <div class="popup-flex-row justify-center"><img height=14 src=${cykel}><span>Klik for at cykle hertil</span></div>
        </div>
      `;
    },
  },
  beforeDestroy() {
    this.udSelected = true;
    this.resetItinerary();
    this.map.remove();
    this.unsubscribe();
  },
  mounted() {
    this.map = createMap('trip-edit-map');
    this.map.on('load', () => {
      loadLayers(this.map);

      const { id } = this.$route.params;
      if (id) {
        this.loadTrip(id);
      } else {
        // Start point from user info

        const lngLat = this.user.institution_placering;
        this.setFromPlace(lngLat);

        if (this.$route.query.toPlace) {
          const payload = {
            title: this.$route.query.toPlace.split(',')[2],
            lat: this.$route.query.toPlace.split(',')[1],
            lng: this.$route.query.toPlace.split(',')[0],
          };
          this.setToPlace(payload);
        }
      }
    });
    this.map.addControl(
      new Mapbox.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: true,
      }),
      'top-left'
    );
    this.map.addControl(
      new Mapbox.ScaleControl({
        maxWidth: 80,
        unit: 'metric',
      }),
      'bottom-right'
    );
    this.map.addControl(new Mapbox.NavigationControl());
    this.map.on('click', e => {
      if (!this.editMode) {
        return;
      }
      const features = this.map.queryRenderedFeatures(e.point);
      const busStops = features.filter(f => f.layer.id === layers.busStops)
        .length;
      if (busStops) {
        return;
      }
      if (this.editMode === 'fromPlace') {
        this.setFromPlace(e.lngLat);
      } else if (this.editMode === 'toPlace') {
        this.setToPlace(e.lngLat);
      }
    });

    this.map.on('click', layers.busStops, e => {
      const busStop = e.features[0];
      if (!busStop.properties.arrival) {
        // Dette er det aktuelle busstoppet vi starter på
        return;
      }
      // const { fromPlace, toPlace } = this.$store.state;
      const from = this.fromPlace.point;
      const to = this.toPlace.point;
      const { arrival, departure, lat, lon, name } = busStop.properties;
      this.$store.commit('setViaPlace', {
        viaPlace: { arrival, departure, lat, lng: lon, name },
        udSelected: this.udSelected,
      });
      this.$store.commit('setTripType', {
        tripType: tripType.bicycleToBusStop,
        udSelected: this.udSelected,
      });
      this.$store
        .dispatch('getItinerariesWithCustomCycleRoute', {
          from,
          to,
          cycleTo: { arrival, departure, lat, lng: lon },
          udSelected: this.udSelected,
          selectedItineraryIndex: this.selectedItineraryIndex,
          startTime: this.startTime,
        })
        .catch(error => {
          this.error = error.msg;
        });
    });
    this.map.on('mouseenter', layers.busStops, e => {
      this.map.getCanvas().style.cursor = 'pointer';

      let coordinates = e.features[0].geometry.coordinates.slice();
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      this.mapPopup
        .setLngLat(coordinates)
        .setHTML(this.getBusStopPopupHtml(e.features[0]))
        .addTo(this.map);
    });
    this.map.on('mouseleave', layers.busStops, () => {
      this.map.getCanvas().style.cursor = '';
      this.mapPopup.remove();
    });

    this.map.on('mouseenter', layers.cykelsti, e => {
      this.map.getCanvas().style.cursor = 'pointer';
      const { lngLat } = e;
      this.mapPopup
        .setLngLat(lngLat)
        .setHTML(this.getCykelstiPopupHtml(e.features[0]))
        .addTo(this.map);
    });
    this.map.on('mouseleave', layers.cykelsti, () => {
      this.map.getCanvas().style.cursor = '';
      this.mapPopup.remove();
    });

    this.map.on('mouseenter', layers.advarsel, e => {
      this.map.getCanvas().style.cursor = 'pointer';
      let coordinates = e.features[0].geometry.coordinates.slice();
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      this.mapPopup
        .setLngLat(coordinates)
        .setHTML(this.getFarligKrydsPopupHtml(e.features[0]))
        .addTo(this.map);
    });
    this.map.on('mouseleave', layers.advarsel, () => {
      this.map.getCanvas().style.cursor = '';
      this.mapPopup.remove();
    });

    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'setSelectedItineraryIndex') {
        const itinerary = this.$store.state.itineraries[
          mutation.payload.selectedItineraryIndex
        ];
        if (this.udSelected && this.tripType === tripType.standard) {
          const cycleLeg = itinerary.legs.find(leg => leg.mode === 'BICYCLE');
          if (cycleLeg) {
            const firstTransit = itinerary.legs.find(
              leg => leg.mode === 'Rail' || leg.mode === 'BUS'
            );
            if (firstTransit) {
              const { arrival, departure, lat, lon, name } = firstTransit.from;
              this.$store.commit('setViaPlace', {
                viaPlace: { arrival, departure, lat, lng: lon, name },
                udSelected: this.udSelected,
              });
            }
          }
        }

        this.setMapItinerary(this.map, itinerary, this.selectedItineraryIndex);
        this.$store.commit('setItineraryModes', {
          itinerary,
          udSelected: this.udSelected,
        });
      }
      if (
        mutation.type === 'setFromPlace' ||
        (mutation.type === 'setToPlace' && !this.loading)
      ) {
        this.getItineraries();
      }
      if (mutation.type === 'stepClicked') {
        showStep(this.map, mutation.payload);
        this.mapViewSelected = true;
      }
    });
  },
};
</script>

<style lang="scss">
.map-view {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: $primary-700;
}
#trip-edit-map {
  width: 100%;
  height: 100%;
  flex-grow: 1;
  flex-basis: 0;
  min-height: 0;
  margin-left: 2px;
}
.content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.vdatetime-input {
  display: none;
}

@media only screen and (max-width: $tablet-landscape-max-width) {
  .content {
    display: block;
    position: relative;
    margin-right: 90px;
  }

  #trip-edit-map {
    position: absolute;
    top: 0;
    left: 0;
    margin-left: 0;
  }
}
@media only screen and (max-width: $mobile-max-width) {
  .content {
    margin-right: 0;
    padding-bottom: 70px;
    margin-bottom: 70px;
  }

  .map-view {
    background-color: $primary-100;
  }
}
</style>
